import React, { useEffect, useState } from 'react';
import { bookingsAPI, publicAPI } from './services/api';
import './PaymentResult.css';

interface BookingDetails {
  reservation_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  product_type: string;
  booking_date: string;
  total_price: number;
  payment_status?: string;
  status?: string;
  room_name?: string;
  meal_bookings?: Array<{
    id: number;
    product_type: string;
    total_price: number;
    booking_date: string;
  }>;
  breakfast_total?: number;
  dinner_total?: number;
  grand_total?: number;
  items?: {
    roomTypeName?: string;
    sunbeds?: number[];
    rowNumber?: number;
    guests?: number;
    guestCount?: number;
    date?: string;
    arrivalTime?: string;
    menuSelections?: any;
    zones?: any[];
    zoneCount?: number;
    totalPersons?: number;
  };
  customer_info?: {
    checkIn?: string;
    checkOut?: string;
    adults?: number;
    children?: number;
    guests?: number;
    guestCount?: number;
    arrivalTime?: string;
    asistproConfirmationCode?: string;
    asistproReservationId?: string | number;
    zoneDetails?: any[];
  };
}

interface ContactSettings {
  hotel_name: string;
  contact_phone: string;
  contact_email: string;
  address: string;
}

const PaymentSuccess: React.FC = () => {
  const [countdown, setCountdown] = useState(30);
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [contactSettings, setContactSettings] = useState<ContactSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);

  const goToHome = () => {
    // Path veya hash tabanlı olabilir, her ikisini de temizle
    window.location.href = '/';
  };

  useEffect(() => {
    // URL'den booking_id al
    const params = new URLSearchParams(window.location.search);
    const bookingId = params.get('booking_id') || params.get('merchant_oid');
    
    console.log('PaymentSuccess - URL:', window.location.href);
    console.log('PaymentSuccess - booking_id:', bookingId);
    
    if (!bookingId) {
      setLoading(false);
      setError('Rezervasyon ID bulunamadı');
      setIsVerifying(false);
      return;
    }

    // Genel ayarları yükle
    publicAPI.getGeneralSettings().then(res => {
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        // Handle both direct object and {key, value} formats
        setContactSettings(data.value || data);
      }
    }).catch(err => console.error('Settings yüklenemedi:', err));

    // Asenkron başlatma fonksiyonu
    const initializeBooking = async () => {
      try {
        // ✅ AsistPro'ya PENDING rezervasyon gönder - AWAIT ile bekle
        console.log('🏨 AsistPro PENDING rezervasyon oluşturuluyor...');
        await bookingsAPI.createPendingReservation(bookingId);
        console.log('✅ AsistPro PENDING rezervasyon başarıyla oluşturuldu');
      } catch (err: any) {
        console.warn('⚠️ AsistPro PENDING oluşturulamadı (callback tekrar deneyecek):', err);
      }
      
      // İlk kontrol - PENDING oluştuktan sonra
      await fetchBookingDetails(bookingId);
    };

    // Başlat
    initializeBooking();

    // Polling: Her 2 saniyede bir kontrol et, max 15 deneme (30 saniye)
    const maxAttempts = 15;
    let attempts = 0;
    const pollingInterval = setInterval(async () => {
      attempts++;
      console.log(`🔄 Payment verification attempt ${attempts}/${maxAttempts}`);

      if (attempts >= maxAttempts) {
        console.warn('⚠️ Payment verification timeout');
        setIsVerifying(false);
        clearInterval(pollingInterval);
        return;
      }

      const verified = await fetchBookingDetails(bookingId);
      if (verified) {
        console.log('✅ Payment verified, stopping polling');
        clearInterval(pollingInterval);
      }
    }, 2000);

    return () => {
      clearInterval(pollingInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown — ödeme doğrulanınca başlar, 0'a gelince ana sayfaya gider
  useEffect(() => {
    if (!paymentVerified) return;
    const countdownTimer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          goToHome();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownTimer);
  }, [paymentVerified]);

  const fetchBookingDetails = async (bookingId: string) => {
    try {
      console.log('Fetching booking details for:', bookingId);
      
      // reservation_id ile arama yap
      const response = await bookingsAPI.getByReservationId(bookingId);
      console.log('Booking API response:', response.data);
      if (response.data.success && response.data.data) {
        const bookingData = response.data.data;
        setBooking(bookingData);
        
        // Ödeme durumunu kontrol et
        if (bookingData.payment_status === 'paid') {
          console.log('✅ Payment verified:', bookingData.payment_status);
          setPaymentVerified(true);
          setIsVerifying(false);
          return true;
        } else {
          console.log('⏳ Payment still pending:', bookingData.payment_status);
          return false;
        }
      } else {
        setError('Rezervasyon bulunamadı');
        setIsVerifying(false);
        return false;
      }
    } catch (error: any) {
      console.error('Rezervasyon detayları alınamadı:', error);
      setError(error.response?.data?.message || 'Rezervasyon detayları yüklenemedi');
      setIsVerifying(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateNights = () => {
    if (!booking?.customer_info?.checkIn || !booking?.customer_info?.checkOut) return 0;
    const checkIn = new Date(booking.customer_info.checkIn);
    const checkOut = new Date(booking.customer_info.checkOut);
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getProductTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      room: '🏨 Oda Rezervasyonu',
      sunbed: '🏖️ Şezlong Kiralama',
      breakfast: '🍳 Kahvaltı Rezervasyonu',
      dinner: '🍽️ Akşam Yemeği Rezervasyonu'
    };
    return labels[type] || type;
  };

  const renderRoomDetails = () => {
    const nights = calculateNights();
    const roomName = booking?.room_name || booking?.items?.roomTypeName || 'Oda';
    const roomPrice = booking?.total_price || 0;
    const breakfastTotal = booking?.breakfast_total || 0;
    const dinnerTotal = booking?.dinner_total || 0;
    const grandTotal = booking?.grand_total || roomPrice;
    const breakfastCount = booking?.meal_bookings?.filter(m => m.product_type === 'breakfast').length || 0;
    const dinnerCount = booking?.meal_bookings?.filter(m => m.product_type === 'dinner').length || 0;
    
    return (
    <>
      <div className="details-grid">
        <div className="detail-item">
          <span className="icon">🏨</span>
          <span className="label">Oda</span>
          <span className="value">{roomName}</span>
        </div>

        {booking?.customer_info?.checkIn && (
          <div className="detail-item">
            <span className="icon">📅</span>
            <span className="label">Giriş</span>
            <span className="value">{formatDate(booking.customer_info.checkIn)}</span>
          </div>
        )}

        {booking?.customer_info?.checkOut && (
          <div className="detail-item">
            <span className="icon">📅</span>
            <span className="label">Çıkış</span>
            <span className="value">{formatDate(booking.customer_info.checkOut)}</span>
          </div>
        )}

        {booking?.customer_info?.adults && (
          <div className="detail-item">
            <span className="icon">👥</span>
            <span className="label">Misafir</span>
            <span className="value">
              {booking.customer_info.adults} Yetişkin
              {booking.customer_info.children ? `, ${booking.customer_info.children} Çocuk` : ''}
            </span>
          </div>
        )}

        <div className="detail-item highlight">
          <span className="icon">🌙</span>
          <span className="label">Konaklama</span>
          <span className="value">{nights} Gece</span>
        </div>
      </div>

      {/* Fiyat Detayları */}
      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <h4 style={{ margin: '0 0 0.75rem 0', color: '#2c3e50' }}>💰 Fiyat Detayları</h4>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid #dee2e6' }}>
          <span>🏨 Oda ({nights} Gece)</span>
          <span style={{ fontWeight: 500 }}>{roomPrice.toLocaleString('tr-TR')} TL</span>
        </div>
        
        {breakfastCount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid #dee2e6' }}>
            <span>🍳 Kahvaltı ({breakfastCount} gün)</span>
            <span style={{ fontWeight: 500 }}>{breakfastTotal.toLocaleString('tr-TR')} TL</span>
          </div>
        )}
        
        {dinnerCount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid #dee2e6' }}>
            <span>🍽️ Akşam Yemeği ({dinnerCount} gün)</span>
            <span style={{ fontWeight: 500 }}>{dinnerTotal.toLocaleString('tr-TR')} TL</span>
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0 0 0', marginTop: '0.4rem', borderTop: '2px solid #2c3e50' }}>
          <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>💳 Toplam Ödenen</span>
          <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#27ae60' }}>{grandTotal.toLocaleString('tr-TR')} TL</span>
        </div>
      </div>

      <div className="checkin-info">
        <h3>🏨 Check-in Bilgileri</h3>
        <p><strong>Giriş Saati:</strong> 14:00'den itibaren</p>
        <p><strong>Çıkış Saati:</strong> 11:00'ye kadar</p>
      </div>
    </>
    );
  };

  const renderSunbedDetails = () => {
    const zoneList = booking?.customer_info?.zoneDetails || booking?.items?.zones || [];
    const hasZones = zoneList.length > 0;
    const totalPersons = booking?.items?.totalPersons || zoneList.reduce((s: number, z: any) => s + (z.person_count || 2), 0);
    return (
    <>
      <div className="details-grid">
        <div className="detail-item">
          <span className="icon">📅</span>
          <span className="label">Tarih</span>
          <span className="value">{formatDate(booking?.booking_date || '')}</span>
        </div>

        {hasZones ? (
          <>
            <div className="detail-item">
              <span className="icon">🏖️</span>
              <span className="label">Bölge Sayısı</span>
              <span className="value">{zoneList.length} bölge</span>
            </div>
            <div className="detail-item">
              <span className="icon">👥</span>
              <span className="label">Toplam Kişi</span>
              <span className="value">{totalPersons} kişi</span>
            </div>
          </>
        ) : booking?.items?.sunbeds && (
          <div className="detail-item">
            <span className="icon">🏖️</span>
            <span className="label">Şezlong Sayısı</span>
            <span className="value">{booking.items.sunbeds.length} adet</span>
          </div>
        )}

        {booking?.customer_info?.arrivalTime && (
          <div className="detail-item">
            <span className="icon">⏰</span>
            <span className="label">Tahmini Varış</span>
            <span className="value">{booking.customer_info.arrivalTime}</span>
          </div>
        )}

        <div className="detail-item total">
          <span className="icon">💳</span>
          <span className="label">Ödenen Tutar</span>
          <span className="value price">{booking?.total_price?.toLocaleString('tr-TR')} TL</span>
        </div>
      </div>

      {/* Bölge Detayları (yeni sistem) */}
      {hasZones && (
        <div className="sunbed-details-section" style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#2c3e50' }}>🏖️ Rezerve Edilen Bölgeler</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {zoneList.map((z: any, index: number) => (
              <div key={index} style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#3498db',
                color: 'white',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                {z.label ? z.label : `Bölge ${z.zone_id}`}
                <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>👤 {z.person_count || 2} kişi</span>
                {z.price ? <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>₺{z.price}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Şezlong Detayları (eski sistem fallback) */}
      {!hasZones && booking?.items?.sunbeds && booking.items.sunbeds.length > 0 && (
        <div className="sunbed-details-section" style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h4 style={{ margin: '0 0 0.75rem 0', color: '#2c3e50' }}>🏖️ Rezerve Edilen Şezlonglar</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {booking.items.sunbeds.map((sunbed: any, index: number) => (
              <div key={index} style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#3498db',
                color: 'white',
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                Sıra {sunbed.row} - No: {sunbed.number}
                <span style={{ marginLeft: '0.5rem', opacity: 0.9 }}>₺{sunbed.price}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="checkin-info">
        <h3>🏖️ Şezlong Bilgileri</h3>
        <p><strong>Kullanım Saatleri:</strong> 09:00 - 19:00</p>
        <p><strong>Konum:</strong> Plaj Alanı</p>
      </div>
    </>
    );
  };

  const renderBreakfastDetails = () => (
    <>
      <div className="details-grid">
        <div className="detail-item">
          <span className="icon">📅</span>
          <span className="label">Tarih</span>
          <span className="value">{formatDate(booking?.booking_date || '')}</span>
        </div>

        {(booking?.items?.guestCount || booking?.customer_info?.guests) && (
          <div className="detail-item">
            <span className="icon">👥</span>
            <span className="label">Kişi Sayısı</span>
            <span className="value">{booking.items?.guestCount || booking.customer_info?.guests} kişi</span>
          </div>
        )}

        {booking?.customer_info?.arrivalTime && (
          <div className="detail-item">
            <span className="icon">⏰</span>
            <span className="label">Geliş Saati</span>
            <span className="value">{booking.customer_info.arrivalTime}</span>
          </div>
        )}

        <div className="detail-item total">
          <span className="icon">💳</span>
          <span className="label">Ödenen Tutar</span>
          <span className="value price">{booking?.total_price?.toLocaleString('tr-TR')} TL</span>
        </div>
      </div>

      <div className="checkin-info">
        <h3>🍳 Kahvaltı Bilgileri</h3>
        <p><strong>Kahvaltı Saatleri:</strong> 08:00 - 11:00</p>
        <p><strong>Konum:</strong> Havuz Başı / Teras</p>
        <p>📍 Geldiğinizde resepsiyona rezervasyon numaranızı söyleyiniz.</p>
      </div>
    </>
  );

  const renderDinnerDetails = () => (
    <>
      <div className="details-grid">
        <div className="detail-item">
          <span className="icon">📅</span>
          <span className="label">Tarih</span>
          <span className="value">{formatDate(booking?.booking_date || '')}</span>
        </div>

        {(booking?.items?.guestCount || booking?.customer_info?.guests) && (
          <div className="detail-item">
            <span className="icon">👥</span>
            <span className="label">Kişi Sayısı</span>
            <span className="value">{booking.items?.guestCount || booking.customer_info?.guests} kişi</span>
          </div>
        )}

        {booking?.customer_info?.arrivalTime && (
          <div className="detail-item">
            <span className="icon">⏰</span>
            <span className="label">Rezervasyon Saati</span>
            <span className="value">{booking.customer_info.arrivalTime}</span>
          </div>
        )}

        <div className="detail-item total">
          <span className="icon">💳</span>
          <span className="label">Ödenen Tutar</span>
          <span className="value price">{booking?.total_price?.toLocaleString('tr-TR')} TL</span>
        </div>
      </div>

      <div className="checkin-info">
        <h3>🍽️ Yemek Bilgileri</h3>
        <p><strong>Akşam Yemeği Saatleri:</strong> 19:00 - 22:00</p>
        <p><strong>Konum:</strong> Ana Restoran</p>
        <p>📍 Geldiğinizde resepsiyona rezervasyon numaranızı söyleyiniz.</p>
      </div>
    </>
  );

  const renderBookingDetails = () => {
    if (!booking) return null;

    switch (booking.product_type) {
      case 'room':
        return renderRoomDetails();
      case 'sunbed':
        return renderSunbedDetails();
      case 'breakfast':
        return renderBreakfastDetails();
      case 'dinner':
        return renderDinnerDetails();
      default:
        return renderRoomDetails();
    }
  };

  return (
    <div className="payment-result-page">
      <div className="payment-result-container success">
        {isVerifying && !paymentVerified ? (
          // Ödeme doğrulama aşaması
          <>
            <div className="success-icon" style={{ animation: 'spin 1s linear infinite' }}>
              <div className="checkmark">⏳</div>
            </div>
            
            <h1>Ödemeniz Doğrulanıyor...</h1>
            
            <p className="result-message">
              Lütfen bekleyin, bankadan onay bekliyoruz.
            </p>
            
            <div className="verification-status" style={{
              padding: '1rem',
              marginTop: '1rem',
              backgroundColor: '#fff3cd',
              borderRadius: '8px',
              border: '1px solid #ffc107'
            }}>
              <p style={{ margin: 0, color: '#856404' }}>
                🔄 Ödeme doğrulanıyor...
              </p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#856404' }}>
                Bu işlem genellikle 2-5 saniye sürer.
              </p>
            </div>
          </>
        ) : !paymentVerified && !isVerifying ? (
          // Timeout durumu - Ödeme doğrulanamadı ama muhtemelen başarılı
          <>
            <div className="success-icon" style={{ backgroundColor: '#ffc107' }}>
              <div className="checkmark">⚠️</div>
            </div>
            
            <h1>Ödeme Doğrulaması Bekleniyor</h1>
            
            <p className="result-message">
              Ödemeniz muhtemelen başarılı oldu, ancak banka onayı henüz ulaşmadı.
            </p>
            
            <div className="info-box warning" style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              padding: '1.5rem',
              marginTop: '1rem'
            }}>
              <p style={{ margin: 0, color: '#856404', fontWeight: 'bold' }}>
                📧 E-posta adresinize onay gelecektir (5-10 dakika içinde)
              </p>
              <p style={{ margin: '1rem 0 0 0', color: '#856404' }}>
                📞 Sorun yaşarsanız: +90 553 406 28 44
              </p>
              {booking && (
                <p style={{ margin: '1rem 0 0 0', color: '#856404', fontWeight: 'bold' }}>
                  🔍 Rezervasyon Numaranız: {booking.reservation_id}
                </p>
              )}
            </div>
          </>
        ) : (
          // Ödeme doğrulandı - Başarılı ekran
          <>
            <div className="success-icon">
              <div className="checkmark">✓</div>
            </div>
            
            <h1>Ödemeniz Alındı!</h1>
            
            <p className="result-message">
              Rezervasyonunuz başarıyla oluşturuldu. Tebrikler! 🎉
            </p>
          </>
        )}

        {loading ? (
          <div className="loading-details">Rezervasyon detayları yükleniyor...</div>
        ) : error ? (
          <div className="info-box warning">
            <p>⚠️ {error}</p>
            <p>📧 Rezervasyon detaylarınız e-posta adresinize gönderildi.</p>
          </div>
        ) : booking ? (
          <div className="booking-details-card">
            <div className="product-type-badge">
              {getProductTypeLabel(booking.product_type)}
            </div>
            
            <div className="reservation-number">
              <span className="label">Rezervasyon No</span>
              <span className="value">{booking.reservation_id}</span>
            </div>

            {renderBookingDetails()}

            <div className="guest-info">
              <p><strong>{booking.customer_name}</strong></p>
              <p>{booking.customer_email}</p>
              {booking.customer_phone && <p>📱 {booking.customer_phone}</p>}
            </div>
          </div>
        ) : (
          <div className="info-box">
            <p>📧 Rezervasyon detaylarınız e-posta adresinize gönderildi.</p>
          </div>
        )}

        {paymentVerified && (
          <>
            <div className="info-box">
              <p>📧 Sorularınız için: {contactSettings?.contact_email || 'info@villapolikne.com'}</p>
              <p>📱 Telefon: {contactSettings?.contact_phone || ''}</p>
              {contactSettings?.address && <p>📍 {contactSettings.address}</p>}
            </div>
            
            <div className="countdown-box">
              <p>{countdown} saniye sonra ana sayfaya yönlendirileceksiniz...</p>
            </div>
          </>
        )}
        
        <button 
          className="btn-return-home"
          onClick={goToHome}
        >
          Ana Sayfaya Dön
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
